import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Book as BookIcon, 
  Assignment as AssignmentIcon, 
  Style as StyleIcon, 
  Notes as NotesIcon,
  ArrowBack,
  Refresh as RefreshIcon,
  Add as AddIcon,
  ChevronLeft,
  ChevronRight,
  Summarize as SummarizeIcon,
  ThumbUp as ThumbUpIcon,
  MenuBook as MenuBookIcon,
  Quiz as QuizIcon
} from '@mui/icons-material';
import { Tooltip, Button } from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../utils/theme-context';
import apiService from '../services/api';
import supabase from '../integrations/supabase';
import FlashcardsModal from '../components/FlashcardsModal';
import QuizModal from '../components/QuizModal';
import RelearnContentCard from '../components/RelearnContentCard';
import { LoadingOverlaySkeleton } from '../components/SkeletonLoader';
import NumberInputModal from '../components/NumberInputModal';
import ExistingContentModal from '../components/ExistingContentModal';

const Study = () => {
  const { subject, book } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { themeTexts, themeIcons } = useTheme();
  const [activeTab, setActiveTab] = useState('chapters');
  const [studyMaterials, setStudyMaterials] = useState({ chapters: [], videos: [], notes: [] });
  const [subjectData, setSubjectData] = useState({});
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [showFlashcards, setShowFlashcards] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // New state for database storage
  const [existingContent, setExistingContent] = useState({});
  const [contentLoading, setContentLoading] = useState({});
  const [contentPages, setContentPages] = useState({});
  const [currentPages, setCurrentPages] = useState({});
  const [generatingContent, setGeneratingContent] = useState({});
  
  // Relearn card state
  const [showRelearnCard, setShowRelearnCard] = useState(false);
  const [relearnCardData, setRelearnCardData] = useState({
    contentType: null,
    subject: null,
    chapter: null
  });

  // Existing content modal state
  const [showExistingContentModal, setShowExistingContentModal] = useState(false);
  const [existingContentData, setExistingContentData] = useState({
    content: [],
    subject: '',
    chapter: ''
  });

  // New state for flashcards data
  const [flashcards, setFlashcards] = useState([]);
  const [flashcardContentId, setFlashcardContentId] = useState(null);
  const [quizContentId, setQuizContentId] = useState(null);
  
  // State for user's course selection
  const [userClass, setUserClass] = useState('');
  
  // Number input modal state
  const [showNumberInput, setShowNumberInput] = useState(false);
  const [numberInputConfig, setNumberInputConfig] = useState({
    title: '',
    description: '',
    contentType: '',
    defaultValue: 10,
    minValue: 5,
    maxValue: 50
  });
  const [selectedChapterForInput, setSelectedChapterForInput] = useState(null);
  
  // Loading states for specific actions
  const [studyLoading, setStudyLoading] = useState(false);
  const [loadingChapter, setLoadingChapter] = useState(null);
  const [flashcardLoading, setFlashcardLoading] = useState(false);
  const [quizLoading, setQuizLoading] = useState(false);

  useEffect(() => {
  
    if (subject) {
      getUserCourseSelection();
    }
  }, [subject, book]);
  
  useEffect(() => {
    if (subject) {
  
      getStudyMaterials();
    }
  }, [subject, userClass, book]);
  
  const getUserCourseSelection = async () => {
    try {
      const { data: courseSelection, error } = await supabase
        .from('course_selections')
        .select('*')
        .eq('user_id', user?.id)
        .single();
      
      if (courseSelection && !error) {
        setUserClass(courseSelection.selected_class || '');
      }
    } catch (error) {

    }
  };

  const getStudyMaterials = async () => {
    const subjectInfo = {
      name: subject,
      icon: '📚',
      description: book ? `${subject} (${book}) study materials` : `${subject} study materials`
    };
    

    
    try {
      // Use API to generate chapters
      const response = await apiService.generateChapters(subject, userClass, book);
      
      
      const chapters = response.chapters || [];
      
      if (chapters.length === 0) {
        // No chapters found from API
        setStudyMaterials({
          chapters: [],
          videos: [],
          notes: []
        });
      } else {
        const mappedChapters = chapters.map((chapter, index) => ({
          id: index + 1,
          title: chapter,
          type: 'chapter'
        }));
        setStudyMaterials({
          chapters: mappedChapters,
          videos: [],
          notes: []
        });
      }
      
      setSubjectData(subjectInfo);
    } catch (error) {
      console.error('Error loading study materials:', error);
      setStudyMaterials({
        chapters: [],
        videos: [],
        notes: []
      });
      setSubjectData(subjectInfo);
    } finally {
      setLoading(false);
    }
  };

  const getExistingContent = async (chapter, contentType) => {
    try {
      const response = await apiService.getExistingContent(contentType, subject, chapter, 1, 10);
      return response.content || [];
    } catch (error) {
      console.error(`Error getting existing ${contentType}:`, error);
      return [];
    }
  };

  const loadMoreContent = async (chapter, contentType) => {
    const currentPage = currentPages[`${chapter}-${contentType}`] || 1;
    const nextPage = currentPage + 1;
    
    try {
      const response = await apiService.getExistingContent(contentType, subject, chapter, nextPage, 10);
      const newContent = response.content || [];
      
      setExistingContent(prev => ({
        ...prev,
        [`${chapter}-${contentType}`]: [
          ...(prev[`${chapter}-${contentType}`] || []),
          ...newContent
        ]
      }));
      
      setCurrentPages(prev => ({
        ...prev,
        [`${chapter}-${contentType}`]: nextPage
      }));
      
      setContentPages(prev => ({
        ...prev,
        [`${chapter}-${contentType}`]: response.total_pages || 1
      }));
    } catch (error) {
      console.error(`Error loading more ${contentType}:`, error);
    }
  };

  const handleRelearn = async (contentId, contentType, chapter) => {
    try {
      let response;
      if (contentType === 'study') {
        response = await apiService.getStudyContentById(contentId);
        navigate(`/study-content/${subject}/${encodeURIComponent(chapter)}`, {
          state: { content: response, isRelearn: true, book: book }
        });
      } else if (contentType === 'flashcard') {
        response = await apiService.getFlashcardsById(contentId);
        setSelectedChapter({ title: chapter });
        setFlashcards(response.flashcards || []);
        setShowFlashcards(true);
      } else if (contentType === 'quiz') {
        response = await apiService.getQuizById(contentId);
        setSelectedChapter({ title: chapter });
        setQuizQuestions(response.questions || []);
        setShowQuiz(true);
      }
    } catch (error) {
      console.error(`Error relearning ${contentType}:`, error);
    }
  };

  const handleGenerateNew = async (contentType, chapter) => {
    setGeneratingContent(prev => ({ ...prev, [`${chapter.title}-${contentType}`]: true }));
    
    try {
      let response;
      if (contentType === 'study') {
        response = await apiService.generateStudyContent(subject, chapter.title);
        navigate(`/study-content/${subject}/${encodeURIComponent(chapter.title)}`, {
          state: { content: response, isNew: true, book: book }
        });
      } else if (contentType === 'flashcard') {
        response = await apiService.generateFlashcards(subject, chapter.title);
        setSelectedChapter(chapter);
        setFlashcards(response.flashcards || []);
        setShowFlashcards(true);
      } else if (contentType === 'quiz') {
        response = await apiService.generateQuiz(subject, chapter.title);
        setSelectedChapter(chapter);
        setQuizQuestions(response.questions || []);
        setShowQuiz(true);
      }
    } catch (error) {
      console.error(`Error generating ${contentType}:`, error);
    } finally {
      setGeneratingContent(prev => ({ ...prev, [`${chapter.title}-${contentType}`]: false }));
    }
  };

  const handleChapterClick = (chapter) => {
    setSelectedChapter(chapter);
    // Load existing content for this chapter
    ['study', 'flashcard', 'quiz'].forEach(contentType => {
      getExistingContent(chapter.title, contentType).then(content => {
        setExistingContent(prev => ({
          ...prev,
          [`${chapter.title}-${contentType}`]: content
        }));
      });
    });
  };

  const handleStudy = async (chapter) => {
    try {
      console.log('🔍 handleStudy called with chapter:', chapter);
      
      // Set loading states
      setStudyLoading(true);
      setLoadingChapter(chapter.title || chapter);
      
      // First check for existing content
      const existingContent = await apiService.getExistingContent('study', subject, chapter.title || chapter, 1, 10);
      console.log('🔍 Existing content response:', existingContent);
      
      if (existingContent.total > 0) {
        console.log('🔍 Found existing content, showing popup');
        // Show existing content popup
        setExistingContentData({
          content: existingContent.data,
          subject: subject,
          chapter: chapter.title || chapter
        });
        setShowExistingContentModal(true);
      } else {
        console.log('🔍 No existing content, generating new');
        // Generate new content
        const response = await apiService.generateStudyContent(subject, chapter.title || chapter);
        navigate(`/study-content/${subject}/${encodeURIComponent(chapter.title || chapter)}`, {
          state: { content: response, book: book }
        });
      }
    } catch (error) {
      console.error('Error generating study content:', error);
    } finally {
      // Clear loading states
      setStudyLoading(false);
      setLoadingChapter(null);
    }
  };

  const handleFlashcards = (chapter) => {
    setNumberInputConfig({
      title: `Generate ${subject} Flashcards`,
      description: `Create flashcards for chapter "${chapter.title || chapter}"`,
      contentType: 'flashcard',
      defaultValue: 10,
      minValue: 5,
      maxValue: 50
    });
    setSelectedChapterForInput(chapter);
    setShowNumberInput(true);
  };

  const handleQuiz = (chapter) => {
    setNumberInputConfig({
      title: `Generate ${subject} Quiz`,
      description: `Create quiz questions for chapter "${chapter.title || chapter}"`,
      contentType: 'quiz',
      defaultValue: 10,
      minValue: 5,
      maxValue: 50
    });
    setSelectedChapterForInput(chapter);
    setShowNumberInput(true);
  };

  const handleNotes = (chapter) => {
    navigate(`/notes?subject=${subject}&chapter=${chapter.title || chapter}`);
  };

  const handleNumberInputConfirm = async (numItems) => {
    if (!selectedChapterForInput) return;
    
    setLoading(true);
    if (numberInputConfig.contentType === 'flashcard') {
      setFlashcardLoading(true);
    } else if (numberInputConfig.contentType === 'quiz') {
      setQuizLoading(true);
    }
    
    try {
      if (numberInputConfig.contentType === 'flashcard') {
        const response = await apiService.generateFlashcards(subject, selectedChapterForInput.title || selectedChapterForInput, numItems);
        setSelectedChapter(selectedChapterForInput); // Set selectedChapter for modal to render
        setFlashcards(response.flashcards || []);
        setFlashcardContentId(response.contentId || null); // Set contentId for like functionality
        setShowFlashcards(true);
      } else if (numberInputConfig.contentType === 'quiz') {
        const response = await apiService.generateQuiz(subject, selectedChapterForInput.title || selectedChapterForInput, numItems);
        setSelectedChapter(selectedChapterForInput); // Set selectedChapter for modal to render
        setQuizQuestions(response.questions || []);
        setQuizContentId(response.contentId || null); // Set contentId for like functionality
        setShowQuiz(true);
      }
    } catch (error) {
      console.error('Error generating content:', error);
    } finally {
      setLoading(false);
      setFlashcardLoading(false);
      setQuizLoading(false);
      setShowNumberInput(false);
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleRelearnContent = (contentType, response, originalContent) => {
    if (contentType === 'study') {
      navigate(`/study-content/${subject}/${encodeURIComponent(relearnCardData.chapter)}`, {
        state: { content: response, isRelearn: true, originalContent, book: book }
      });
    } else if (contentType === 'flashcard') {
      setSelectedChapter({ title: relearnCardData.chapter });
      setFlashcards(response.flashcards || []);
      setShowFlashcards(true);
    } else if (contentType === 'quiz') {
      setSelectedChapter({ title: relearnCardData.chapter });
      setQuizQuestions(response.questions || []);
      setShowQuiz(true);
    }
    setShowRelearnCard(false);
  };

  const handleRegenerateContent = async (contentType) => {
    try {
      if (contentType === 'study') {
        const response = await apiService.regenerateStudyContent(subject, relearnCardData.chapter);
        navigate(`/study-content/${subject}/${encodeURIComponent(relearnCardData.chapter)}`, {
          state: { content: response, isNew: true }
        });
      } else if (contentType === 'flashcard') {
        const response = await apiService.generateFlashcards(subject, relearnCardData.chapter);
        setSelectedChapter({ title: relearnCardData.chapter });
        setFlashcards(response.flashcards || []);
        setShowFlashcards(true);
      } else if (contentType === 'quiz') {
        const response = await apiService.generateQuiz(subject, relearnCardData.chapter);
        setSelectedChapter({ title: relearnCardData.chapter });
        setQuizQuestions(response.questions || []);
        setShowQuiz(true);
      }
      setShowRelearnCard(false);
    } catch (error) {
      console.error(`Error regenerating ${contentType}:`, error);
    }
  };

  const handleCloseRelearnCard = () => {
    setShowRelearnCard(false);
    setRelearnCardData({
      contentType: null,
      subject: null,
      chapter: null
    });
  };

  const handleSelectExistingContent = (content) => {
    navigate(`/study-content/${subject}/${encodeURIComponent(content.chapter || existingContentData.chapter)}`, {
      state: { 
        content: content.content,
        isExisting: true,
        contentId: content.id,
        book: book
      }
    });
    setShowExistingContentModal(false);
  };

  const handleGenerateNewContent = async () => {
    try {
      const response = await apiService.generateStudyContent(subject, existingContentData.chapter);
      navigate(`/study-content/${subject}/${encodeURIComponent(existingContentData.chapter)}`, {
        state: { content: response, book: book }
      });
      setShowExistingContentModal(false);
    } catch (error) {
      console.error('Error generating new content:', error);
    }
  };

  console.log('🔧 Study page render - loading:', loading, 'studyMaterials:', studyMaterials);
  
  if (loading) {
    return <LoadingOverlaySkeleton />;
  }

  console.log('🔧 Study component render - studyMaterials.chapters.length:', studyMaterials.chapters.length);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/60 dark:bg-gray-900/60 backdrop-blur-lg rounded-2xl shadow-lg p-3 sm:p-4 mb-6 cursor-pointer hover:bg-white/80 dark:hover:bg-gray-900/80 transition-all duration-200"
            onClick={handleBack}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-2 rounded-full bg-gray-100 dark:bg-gray-700">
                  <ArrowBack className="text-gray-600 dark:text-gray-400" />
                </div>
                <div>
  
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  </p>
                </div>
              </div>
              {book && (
                <div className="flex items-center">
                  <div className="bg-teal-100 dark:bg-teal-900/30 px-4 py-2 rounded-lg border border-teal-200 dark:border-teal-700">
                    <span className="text-base sm:text-lg font-bold text-teal-700 dark:text-teal-300">
                      📚 {userClass} > {subject} > {book}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Tab Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/60 dark:bg-gray-900/60 backdrop-blur-lg rounded-2xl shadow-lg p-3 sm:p-4 mb-6"
          >
            <div className="flex space-x-1 sm:space-x-2">
              {[
                { key: 'chapters', label: 'Chapters', smLabel: 'Chapters' },
                { key: 'videos', label: 'Videos', smLabel: 'Videos' },
                { key: 'notes', label: 'My Notes', smLabel: 'My Notes' }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 px-4 sm:px-6 py-2 sm:py-3 rounded-xl text-sm sm:text-base font-medium transition-all duration-200 ${
                    activeTab === tab.key
                      ? 'bg-teal-500 text-white shadow-lg'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.smLabel}</span>
                </button>
              ))}
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-4"
          >
            {activeTab === 'chapters' && (
              <div className="bg-white/60 dark:bg-gray-900/60 backdrop-blur-lg rounded-2xl shadow-lg p-3 sm:p-4">
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {studyMaterials.chapters.length === 0 ? (
                    <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">
                      <p>No chapters found for {subject}</p>
                      <p className="text-sm mt-2">Loading chapters from curriculum...</p>
                    </div>
                  ) : (
                    studyMaterials.chapters.map((chapter) => (
                    <motion.div
                      key={chapter.id}
                      whileHover={{ scale: 1.03, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      className="bg-white/80 dark:bg-gray-800/80 rounded-xl p-4 border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:border-teal-200 dark:hover:border-teal-600 transition-all duration-300 group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="p-2 bg-gradient-to-br from-teal-100 to-blue-100 dark:from-teal-900/30 dark:to-blue-900/30 rounded-lg flex-shrink-0 group-hover:scale-110 transition-transform duration-200">
                            <BookIcon className="text-teal-600 dark:text-teal-400 text-base" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <Tooltip title={chapter.title} arrow placement="top">
                              <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-sm truncate group-hover:text-teal-700 dark:group-hover:text-teal-300 transition-colors duration-200">
                                {chapter.title}
                              </h3>
                            </Tooltip>
                          </div>
                        </div>
                        
                        <div className="flex gap-1">
                          <Tooltip title={studyLoading && loadingChapter === chapter.title ? "Loading..." : "Study"} arrow>
                            <button
                              onClick={() => handleStudy(chapter)}
                              disabled={studyLoading}
                              className={`touch-target p-1.5 rounded-md transition-all duration-200 ease-in-out hover:scale-110 transform ${
                                studyLoading && loadingChapter === chapter.title
                                  ? 'bg-teal-200 dark:bg-teal-800 cursor-not-allowed'
                                  : 'hover:bg-teal-100 dark:hover:bg-teal-900/50 focus:bg-teal-200 dark:focus:bg-teal-800/50'
                              }`}
                              aria-label={`Study ${chapter.title}`}
                            >
                              {studyLoading && loadingChapter === chapter.title ? (
                                <div className="w-4 h-4 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <MenuBookIcon className="text-teal-600 dark:text-teal-300 w-4 h-4" />
                              )}
                            </button>
                          </Tooltip>
                          <Tooltip title="Quiz" arrow>
                            <button
                              onClick={() => handleQuiz(chapter)}
                              disabled={quizLoading}
                              className="touch-target p-1.5 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/50 focus:bg-blue-200 dark:focus:bg-blue-800/50 transition-all duration-200 ease-in-out hover:scale-110 transform"
                              aria-label={`Quiz for ${chapter.title}`}
                            >
                              <QuizIcon className="text-blue-600 dark:text-blue-300 w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip title="Flashcards" arrow>
                            <button
                              onClick={() => handleFlashcards(chapter)}
                              disabled={flashcardLoading}
                              className="touch-target p-1.5 rounded-md hover:bg-purple-100 dark:hover:bg-purple-900/50 focus:bg-purple-200 dark:focus:bg-purple-800/50 transition-all duration-200 ease-in-out hover:scale-110 transform"
                              aria-label={`Flashcards for ${chapter.title}`}
                            >
                              <StyleIcon className="text-purple-600 dark:text-purple-300 w-4 h-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </div>
                    </motion.div>
                  ))
                  )}
                </div>
              </div>
            )}

            {activeTab === 'videos' && (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                Videos coming soon...
              </div>
            )}

            {activeTab === 'notes' && (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                Notes coming soon...
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Relearn Content Card */}
      {showRelearnCard && relearnCardData.contentType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="max-w-2xl w-full">
            <RelearnContentCard
              subject={relearnCardData.subject}
              chapter={relearnCardData.chapter}
              contentType={relearnCardData.contentType}
              onRelearn={handleRelearnContent}
              onRegenerate={handleRegenerateContent}
              onClose={handleCloseRelearnCard}
            />
          </div>
        </div>
      )}

      {/* Modals */}
      {selectedChapter && (
        <>
          <FlashcardsModal
            isOpen={showFlashcards}
            onClose={() => {
              setShowFlashcards(false);
              setSelectedChapter(null); // Clear selectedChapter when modal closes
              setFlashcardContentId(null); // Clear contentId when modal closes
            }}
            subject={subject}
            chapter={selectedChapter.title}
            flashcards={flashcards}
            isExisting={flashcards.length > 0}
            onRegenerate={() => handleRegenerateContent('flashcard')}
            contentId={flashcardContentId}
          />
          <QuizModal
            isOpen={showQuiz}
            onClose={() => {
              setShowQuiz(false);
              setSelectedChapter(null); // Clear selectedChapter when modal closes
              setQuizContentId(null); // Clear contentId when modal closes
            }}
            subject={subject}
            chapter={selectedChapter.title}
            questions={quizQuestions}
            isExisting={quizQuestions.length > 0}
            contentId={quizContentId}
          />
        </>
      )}

      {/* Existing Content Modal */}
      <ExistingContentModal
        isOpen={showExistingContentModal}
        onClose={() => setShowExistingContentModal(false)}
        existingContent={existingContentData.content}
        subject={existingContentData.subject}
        chapter={existingContentData.chapter}
        onSelectContent={handleSelectExistingContent}
        onGenerateNew={handleGenerateNewContent}
      />

      {/* Number Input Modal */}
      <NumberInputModal
        isOpen={showNumberInput}
        onClose={() => setShowNumberInput(false)}
        onConfirm={handleNumberInputConfirm}
        title={numberInputConfig.title}
        description={numberInputConfig.description}
        defaultValue={numberInputConfig.defaultValue}
        minValue={numberInputConfig.minValue}
        maxValue={numberInputConfig.maxValue}
      />

      {/* Study Loading Overlay */}
      {studyLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-2xl max-w-md w-full mx-4">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-2">
                Generating Summary
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {loadingChapter ? `Creating summary for "${loadingChapter}"...` : "Please wait while we generate your study content..."}
              </p>
              <div className="mt-4 flex justify-center">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-teal-600 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-teal-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-teal-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Study; 